import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { Divider, Grid, List, ListItem, ListItemText, Paper, Stack, Typography } from '@mui/material';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import Button from '@mui/material/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import { getLineItemValidationSchema } from '../../validations/LineItemValidation';
import CustomTextInput from '../CustomTextInput';
import { ALLOWANCE, CHARGE, DEFAULT_TAX_SCHEME } from '../../constants/defaults';
import { formatXmlUnitPrice, NUMERIC_TEXT_SX } from '../../utils/currencyFormat';
import TaxCategoryFields from '../TaxCategoryField';
import AllowanceChargeSection from './AllowanceChargeSection';
import AddAllowanceChargeModal from './AddAllowanceChargeModal';
import { lineItemCalculation, resolvePriceBaseAmount } from '../../utils/pricingEngineForDocuments';
import UOMSelect from './UOMSelect';
import { useSelector } from 'react-redux';
import { getUOMOptionByCode, getUOMOptions } from '../../api/codelist/codeListsSlice';
import ProductSelect from './ProductSelect';
import GoodsOrServiceTypeSelect from './GoodOrServiceTypeSelect';
import ClassificationCodeSelect from './ClassificationCode';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';
import CustomSelectInput from '../CustomSelectInput';

const STANDARD_ITEM_SCHEME_ID = '0160';
const REVERSE_CHARGE_TAX_CATEGORY_ID = 'AE';
const initialState = {
  quantity: 1,
  unit_code: null,
  description: '',
  name: '',
  classified_tax_category: {
    id: null,
    percent: 0,
    tax_exemption_reason_code: null,
    tax_exemption_reason: null,
    tax_scheme: DEFAULT_TAX_SCHEME,
  },
  price: {
    base_amount: 1,
    base_quantity: 1,
    allowance: {
      amount: 0,
      base_amount: 1,
      reason_code: null,
      reason: null,
      taxCategory: null,
    },
  },
  allowances: [],
  charges: [],
};

const LineItemForm = ({
  submitForm,
  defaultState,
  readOnly = false,
  edit = false,
  invoiceTypeCode,
  profileExecutionId,
  externalFieldErrors = {},
  restrictToRcm = false,
}) => {
  const { eInvoiceForm, validations } = useTranslatedMessages();
  const validationSchema = useMemo(() => getLineItemValidationSchema(validations), [validations]);
  const { line_item_form: t } = eInvoiceForm;
  const uomOption = useSelector(getUOMOptionByCode(defaultState?.unit_code));

  const rcmDefaultTaxCategory = {
    id: REVERSE_CHARGE_TAX_CATEGORY_ID,
    percent: 0,
    tax_exemption_reason_code: null,
    tax_exemption_reason: null,
    tax_scheme: DEFAULT_TAX_SCHEME,
  };

  const defaultValues = defaultState
    ? {
        ...defaultState,
        allowances: defaultState?.allowances?.length ? defaultState?.allowances : [],
        charges: defaultState?.charges?.length ? defaultState?.charges : [],
        unit_code: uomOption ?? '',
        classified_tax_category: restrictToRcm
          ? {
              ...defaultState?.classified_tax_category,
              ...rcmDefaultTaxCategory,
            }
          : defaultState?.classified_tax_category,
        commodity_code: defaultState?.commodity_classification?.commodity_code || null,
        item_classification_identifier: defaultState?.commodity_classification?.item_classification_code
          ? {
              code: defaultState.commodity_classification.item_classification_code,
            }
          : null,
        service_accounting_code: defaultState?.additional_item_identification?.[0]?.id
          ? {
              code: defaultState.additional_item_identification[0].id,
            }
          : null,
        price: defaultState?.price
          ? {
              base_amount: resolvePriceBaseAmount(defaultState.price),
              base_quantity: defaultState.price.base_quantity || 1,
              allowance: {
                amount: defaultState.price?.allowance?.amount,
                base_amount: defaultState.price?.allowance?.base_amount ?? resolvePriceBaseAmount(defaultState.price),
                reason_code: defaultState.price?.allowance?.reason_code,
                reason: defaultState.price?.allowance?.reason,
                taxCategory: defaultState.price?.allowance?.taxCategory,
              },
            }
          : initialState.price,
        nature_code: defaultState?.commodity_classification?.nature_code || null,
      }
    : {
        ...initialState,
        ...(restrictToRcm && { classified_tax_category: rcmDefaultTaxCategory }),
      };
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    clearErrors,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    context: {
      invoice_type_code: invoiceTypeCode,
      profile_execution_id: profileExecutionId,
    },
  });

  useEffect(() => {
    Object.entries(externalFieldErrors).forEach(([fieldName, message]) => {
      setError(fieldName, { message, type: 'string' });
    });
  }, [externalFieldErrors, setError]);

  const {
    fields: allowanceFields,
    append: appendAllowance,
    remove: removeAllowance,
    update: updateAllowance,
  } = useFieldArray({
    control,
    name: 'allowances',
  });

  const {
    fields: chargeFields,
    append: appendCharge,
    remove: removeCharge,
    update: updateCharge,
  } = useFieldArray({
    control,
    name: 'charges',
  });

  // Modal state
  const [allowanceModalOpen, setAllowanceModalOpen] = useState(false);
  const [allowanceType, setAllowanceType] = useState(ALLOWANCE);
  const [editingAllowanceIndex, setEditingAllowanceIndex] = useState();

  const lineItemData = useWatch({ control });
  const pricingEngine = lineItemCalculation(lineItemData);
  const values = lineItemData;

  const handleAdd = () => {
    const data = pricingEngine;
    const hsn =
      typeof values.item_classification_identifier === 'object'
        ? values.item_classification_identifier?.code
        : values.item_classification_identifier || null;
    const sac = values.service_accounting_code?.code || values.service_accounting_code;
    const isReverseCharge = data.classified_tax_category.id === REVERSE_CHARGE_TAX_CATEGORY_ID;
    const natureCode = isReverseCharge ? data.nature_code : null;
    const hasCommodityClassification = values.commodity_code || hsn || natureCode;

    const {
      nature_code: _natureCode,
      commodity_code: _commodityCode,
      item_classification_identifier: _itemClassificationIdentifier,
      service_accounting_code: _serviceAccountingCode,
      ...lineItemData
    } = data;

    submitForm({
      ...lineItemData,
      unit_code: data.unit_code?.code,

      ...(hasCommodityClassification && {
        commodity_classification: {
          ...(natureCode && { nature_code: natureCode }),
          ...(values.commodity_code && { commodity_code: values.commodity_code }),
          ...(hsn && {
            item_classification_code: hsn,
            item_classification_list_id: 'HS',
            item_classification_list_version_id: null,
          }),
        },
      }),

      additional_item_identification: sac
        ? [
            {
              id: sac,
              scheme_id: 'SAC',
              scheme_version_id: '1.0',
            },
          ]
        : [],
      ...(isReverseCharge && {
        standard_item_identification: { scheme_id: STANDARD_ITEM_SCHEME_ID },
      }),
    });
    reset(initialState);
  };

  const handleReset = () => {
    reset(defaultValues);
  };
  const handleAddAllowance = (allowance) => {
    if (allowanceType === ALLOWANCE) {
      if (editingAllowanceIndex !== undefined) {
        updateAllowance(editingAllowanceIndex, allowance);
        setEditingAllowanceIndex(undefined);
      } else {
        appendAllowance(allowance);
      }
    } else {
      if (editingAllowanceIndex !== undefined) {
        updateCharge(editingAllowanceIndex, allowance);
        setEditingAllowanceIndex(undefined);
      } else {
        appendCharge(allowance);
      }
    }
  };

  const handleOpenDiscountModal = () => {
    setAllowanceType(ALLOWANCE);
    setEditingAllowanceIndex(undefined);
    setAllowanceModalOpen(true);
  };

  const handleOpenChargeModal = () => {
    setAllowanceType(CHARGE);
    setEditingAllowanceIndex(undefined);
    setAllowanceModalOpen(true);
  };

  const handleEditAllowance = (index, type) => {
    setAllowanceType(type);
    setEditingAllowanceIndex(index);
    setAllowanceModalOpen(true);
  };

  const handleDeleteAllowance = (index, type) => {
    if (type === ALLOWANCE) {
      removeAllowance(index);
    } else {
      removeCharge(index);
    }
  };
  const uomOptions = useSelector(getUOMOptions);

  const onProductSelect = (product) => {
    if (!product) return;

    setValue('name', product?.name || '');
    setValue('description', product?.description || '');
    setValue('price.base_amount', product?.price?.base_amount ?? product?.unit_price ?? 1);
    setValue('quantity', product?.quantity || 1);
    const uomObj = uomOptions.find((u) => u.code === product?.unit_of_measure);
    setValue('unit_code', uomObj || null);

    const commCode =
      product?.commodity_code?.value ||
      product?.commodity_code ||
      product?.commodity_classification?.commodity_code ||
      null;
    setValue('commodity_code', commCode);

    const hsn =
      product?.item_classification_identifier?.code ||
      product?.item_classification_identifier ||
      product?.commodity_classification?.item_classification_code ||
      null;
    setValue('item_classification_identifier', hsn);

    const sacEntry = product?.additional_item_identification?.find((i) => i.scheme_id === 'SAC');
    const sac =
      product?.service_accounting_code?.code ||
      product?.service_accounting_code ||
      sacEntry?.id ||
      product?.additional_item_identification?.[0]?.id ||
      null;
    setValue('service_accounting_code', sac);

    const taxCategory = product?.classified_tax_category;
    const isAE = taxCategory?.id === 'AE';
    setValue('classified_tax_category.id', taxCategory?.id ?? null);
    setValue('classified_tax_category.percent', taxCategory?.percent ?? 0);
    setValue('classified_tax_category.tax_scheme', taxCategory?.tax_scheme ?? DEFAULT_TAX_SCHEME);
    setValue('classified_tax_category.tax_exemption_reason_code', taxCategory?.tax_exemption_reason_code ?? null);
    setValue('classified_tax_category.tax_exemption_reason', taxCategory?.tax_exemption_reason ?? null);

    const natureCode = isAE ? product?.commodity_classification?.nature_code || product?.nature_code || null : null;
    setValue('nature_code', natureCode);
  };

  const onNameChange = (name) => {
    setValue('name', name || '');
  };
  const taxCategory = useWatch({ control, name: 'classified_tax_category.id' });
  const itemType = watch('commodity_code');
  const isBoth = itemType === 'B';
  const gridSize = isBoth ? 4 : 6;
  const mainGridSize = isBoth ? 4 : 6;

  useEffect(() => {
    if (!edit) {
      if (itemType === 'G') {
        setValue('service_accounting_code', null);
      } else if (itemType === 'S') {
        setValue('item_classification_identifier', null);
      }
    }
  }, [itemType, edit, setValue]);

  useEffect(() => {
    if (taxCategory !== REVERSE_CHARGE_TAX_CATEGORY_ID) {
      setValue('nature_code', null);
    }
  }, [taxCategory, setValue]);

  return (
    <>
      <Paper elevation={0} sx={{ p: '1rem', overflow: 'auto' }}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <ProductSelect
                data-testid="name-input"
                name="name"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={t.item_name_label}
                onProductSelect={onProductSelect}
                onNameChange={onNameChange}
              />
            </Grid>
            <Grid size={6}>
              <CustomTextInput
                data-testid="description-input"
                name="description"
                control={control}
                errors={errors}
                readOnly={readOnly}
                label={t.description_label}
              />
            </Grid>
            <Grid size={mainGridSize}>
              <CustomSelectInput
                name="commodity_code"
                control={control}
                errors={errors}
                label="Item Type"
                readOnly={readOnly}
                options={[
                  { label: 'Goods', value: 'G' },
                  { label: 'Services', value: 'S' },
                  { label: 'Both', value: 'B' },
                ]}
              />
            </Grid>

            {(itemType === 'G' || itemType === 'B') && (
              <Grid size={gridSize}>
                <ClassificationCodeSelect
                  label="Item Classification Identifier"
                  name="item_classification_identifier"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                />
              </Grid>
            )}

            {(itemType === 'S' || itemType === 'B') && (
              <Grid size={gridSize}>
                <ClassificationCodeSelect
                  label="Service Accounting Code"
                  name="service_accounting_code"
                  control={control}
                  errors={errors}
                  readOnly={readOnly}
                />
              </Grid>
            )}

            {!itemType && <Grid size={6} />}
            <Grid size={4}>
              <UOMSelect control={control} errors={errors} name="unit_code" readOnly={readOnly} />
            </Grid>
            <Grid size={4}>
              <CustomTextInput
                control={control}
                data-testid="quantity-input"
                name="quantity"
                label={t.quantity_label}
                type="number"
                errors={errors}
                readOnly={readOnly}
              />
            </Grid>
            <Grid size={4}>
              <CustomTextInput
                control={control}
                data-testid="unit-price-input"
                name="price.base_amount"
                label={t.unit_price_label}
                type="number"
                errors={errors}
                readOnly={readOnly}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
            {t.allowances_and_charges_title}
            <Divider />
          </Typography>

          <AllowanceChargeSection
            data-testid="charges-section"
            title={t.charges_title}
            type={CHARGE}
            fields={chargeFields}
            onAdd={handleOpenChargeModal}
            onEdit={handleEditAllowance}
            onDelete={handleDeleteAllowance}
            readOnly={readOnly}
          />

          <AllowanceChargeSection
            data-testid="allowances-section"
            title={t.allowances_title}
            type={ALLOWANCE}
            fields={allowanceFields}
            onAdd={handleOpenDiscountModal}
            onEdit={handleEditAllowance}
            onDelete={handleDeleteAllowance}
            readOnly={readOnly}
          />

          <Typography variant="h6" sx={{ mb: '1.5rem', pt: '1rem' }}>
            {t.taxes_title}
            <Divider />
          </Typography>

          <Grid container spacing={2} my={2}>
            <TaxCategoryFields
              data-testid="tax-category-fields"
              control={control}
              errors={errors}
              taxCategoryName="classified_tax_category.id"
              taxRateName="classified_tax_category.percent"
              setValue={setValue}
              getValues={getValues}
              clearErrors={clearErrors}
              taxCategoryReadOnly={readOnly}
              taxRateReadOnly={readOnly}
              invoiceTypeCode={invoiceTypeCode}
              profileExecutionId={profileExecutionId}
              restrictToRcm={restrictToRcm}
            />
          </Grid>
          {taxCategory == REVERSE_CHARGE_TAX_CATEGORY_ID && (
            <Grid size={4}>
              <GoodsOrServiceTypeSelect
                control={control}
                name="nature_code"
                label={t.goods_or_service_type_label}
                error={!!errors?.nature_code}
                helperText={typeof errors?.nature_code?.message === 'string' ? errors.nature_code.message : ''}
                readOnly={readOnly}
              />
            </Grid>
          )}
        </Box>

        <Divider sx={{ py: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <List sx={{ width: '50%' }}>
            <ListItem>
              <ListItemText data-testid="base-amount" primary={t.base_amount} />
              <Typography variant="body1" sx={NUMERIC_TEXT_SX}>
                {formatXmlUnitPrice(pricingEngine.base_amount)}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText data-testid="calculated-charge" primary={t.charge} />
              <Typography variant="body1" sx={NUMERIC_TEXT_SX}>
                {formatXmlUnitPrice(pricingEngine.charge_amount)}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText data-testid="calculated-allowance" primary={t.allowance} />
              <Typography variant="body1" color="error" sx={NUMERIC_TEXT_SX}>
                {'\u200E'}-{formatXmlUnitPrice(pricingEngine.allowance_amount)}
                {'\u200E'}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText data-testid="calculated-total-before-tax" primary={t.total_before_tax} />
              <Typography variant="body1" sx={NUMERIC_TEXT_SX}>
                {formatXmlUnitPrice(pricingEngine.net_amount)}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemText data-testid="calculated-tax" primary={t.tax} />
              <Typography variant="body1" sx={NUMERIC_TEXT_SX}>
                {formatXmlUnitPrice(pricingEngine.tax_amount)}
              </Typography>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: 'h6' }}
                data-testid="calculated-sub-total"
                primary={t.sub_total}
              />
              <Typography
                variant="h6"
                color={pricingEngine.total_amount < 0 ? 'error' : 'inherit'}
                sx={NUMERIC_TEXT_SX}
              >
                {formatXmlUnitPrice(pricingEngine.total_amount)}
              </Typography>
            </ListItem>
          </List>
        </Box>
      </Paper>

      <Divider />

      <Stack direction="row" alignItems="center" spacing={1} p={2}>
        <Box flexGrow={1}>
          {pricingEngine.total_amount < 0 && (
            <Typography variant="body2" color="error" fontWeight={500}>
              {t.negative_sub_total_error}
            </Typography>
          )}
        </Box>

        {!readOnly && (
          <Stack direction="row" spacing={1}>
            <Button data-testid="reset-button" type="button" variant="outlined" color="primary" onClick={handleReset}>
              {t.reset_button}
            </Button>
            <Button
              data-testid="add-button"
              type="button"
              variant="contained"
              color="primary"
              disabled={pricingEngine.total_amount < 0}
              onClick={handleSubmit(handleAdd)}
            >
              {edit ? t.update_button : t.add_button}
            </Button>
          </Stack>
        )}
      </Stack>

      <AddAllowanceChargeModal
        open={allowanceModalOpen}
        onClose={() => setAllowanceModalOpen(false)}
        onAdd={handleAddAllowance}
        type={allowanceType}
        initialFormData={
          editingAllowanceIndex !== undefined
            ? allowanceType === ALLOWANCE
              ? getValues(`allowances.${editingAllowanceIndex}`)
              : getValues(`charges.${editingAllowanceIndex}`)
            : undefined
        }
        editIndex={editingAllowanceIndex}
        currentNetAmount={pricingEngine.net_amount}
        itemBaseAmount={pricingEngine.base_amount}
        invoiceTypeCode={invoiceTypeCode}
        profileExecutionId={profileExecutionId}
      />
    </>
  );
};

export default LineItemForm;
